/**
 * LoginButton — shows "Kirjaudu sisään" when unauthenticated,
 * user avatar + name when authenticated with a logout menu.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PlaceIcon from '@mui/icons-material/Place';
import { AuthUser } from '../services/authService';

interface LoginButtonProps {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  onLogin: () => void;
  onLogout: () => void;
}

export default function LoginButton({
  user,
  isAuthenticated,
  isLoading,
  onLogin,
  onLogout,
}: LoginButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  if (isLoading) {
    return <CircularProgress size={24} color="inherit" />;
  }

  if (!isAuthenticated || !user) {
    return (
      <Button
        color="inherit"
        startIcon={<LoginIcon />}
        onClick={onLogin}
        sx={{ minWidth: { xs: 'auto', sm: 'auto' }, minHeight: 44 }}
        aria-label="Kirjaudu sisään"
      >
        <Typography
          variant="body2"
          sx={{ display: { xs: 'none', sm: 'inline' } }}
        >
          Kirjaudu sisään
        </Typography>
      </Button>
    );
  }

  // Authenticated — show avatar + name
  const displayName = user.name || user.email;
  const avatarSrc = user.picture || undefined;

  return (
    <>
      <Box
        onClick={handleAvatarClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          minHeight: 44,
          minWidth: 44,
          px: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
        }}
        role="button"
        aria-label="Käyttäjävalikko"
        aria-haspopup="true"
        aria-expanded={menuOpen}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAvatarClick(e as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
      >
        <Avatar
          src={avatarSrc}
          alt={displayName}
          sx={{ width: 32, height: 32 }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Typography
          variant="body2"
          color="inherit"
          sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 120 }}
          noWrap
        >
          {displayName}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); navigate('/profiles'); }}>
          <ListItemIcon>
            <PlaceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sijaintiprofiilit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Kirjaudu ulos</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
